module Steps.Step3.FormView
    ( renderFormFill
    ) where

import Common

import Concur.React.DOM as D
import Concur.React.Props as P
import API.Search (SearchSemester, SearchTeacher)
import Data.Array (catMaybes)
import Internal.Luxon as DT
import Steps.Step3.Editability (LockedBanner(..))
import Steps.Step3.Types (FillQueueState, FormFillResult(..), FormFillState, formContext, formEditability)
import Steps.Step3.Types as Step3Types
import Steps.WorkQueue.State (WorkQueueState, formForSelected')
import Steps.WorkQueue.UIState (WorkQueueUiState)
import Steps.WorkQueue.View (WorkQueueResult(..))
import Widgets.BetterSelect (Select, selectedItem)
import Widgets.Attachments (runAttachments)
import Widgets.Attachments as Attachments
import Widgets.Comments (runComments)
import Widgets.Comments as Comments
import Widgets.Form (FormState, formWidget)
import Widgets.Form.Access (FieldAccessLevel(..))
import Widgets.Form.Class (FormWidgetResult(..))
import Widgets.FormPreview (PreviewState, renderPreview)
import Widgets.HiddenSelect (hiddenSelect, hiddenSelectDisabled, hiddenSelectLabel)
import Widgets.Loading (loadingWidget)
import Widgets.Signature as Signature
import Widgets.Signature.Group (signatureGroup)
import Widgets.Signature.Group as SigGroup

type WorkQueueMetadataDraft =
    { state :: WorkQueueState
    , ui :: WorkQueueUiState
    }

renderFormFill :: Boolean -> Boolean -> FillQueueState -> Widget HTML FormFillResult
renderFormFill signatures busy state =
    renderFormFillState signatures busy state

renderFormFillState :: Boolean -> Boolean -> FillQueueState -> Widget HTML FormFillResult
renderFormFillState signatures busy state = D.div
    [ P.className "step-3-right form-submission relative" ]
    $ renderLockBanner busy fillState
    <> (if formForSelected' state.workQueueState
        then []
        else [ D.div [ P.className "error" ] [ D.text $ lookupTranslation "must-select-teacher-semester" ] ])
    <> [ D.div
            [ P.className "teacher-semester-selections" ]
            [ D.p' [ D.text $ lookupTranslation semesterTranslation ]
            , if busy && fillState.doc.can.update
                then hiddenSelectDisabled state.workQueueUi.semester
              else if fillState.doc.can.update
                then metadataStateChangeF state <$> setSemesterSelect state <$> hiddenSelect state.workQueueUi.semester
                else hiddenSelectLabel state.workQueueUi.semester
            , D.p' [ D.text $ lookupTranslation "teacher-hidden-select" ]
            , if busy && fillState.doc.can.update
                then hiddenSelectDisabled state.workQueueUi.teacher
              else if fillState.doc.can.update
                then metadataStateChangeF state <$> setTeacherSelect state <$> hiddenSelect state.workQueueUi.teacher
                else hiddenSelectLabel state.workQueueUi.teacher
            ]
        , formResultF fillState <$> formWidget formContextForBusy fillState.form
        ]
    <> if not signatures then []
        else
            [ D.p' [ D.text $ lookupTranslation "save-before-preview" ]
            , D.a [ P._id "signature-anchor"] []
            , previewStateChangeF fillState <$> renderPreview fillState.preview
            , D.div
                [ P.className "signatures" ] $
                if length fillState.signatures.sigs > 0
                    then
                        [ D.h1
                            [ P.className "signatures-heading" ]
                            [ D.text $ lookupTranslation "sign" ]
                        , sigResultF fillState <$> signatureGroup fillState.signatures
                        ]
                    else []
            , commentResultF fillState <$> runComments fillState.comments
            , attachmentResultF fillState <$> runAttachments fillState.attachments
            , D.div
                [ P.className "form-updated-times" ]
                [ D.p' [ D.text $ lookupTranslation "form-created-at" <> " " <> DT.prettyPrint fillState.doc.created_at ]
                , D.p' [ D.text $ lookupTranslation "form-updated-at" <> " " <> DT.prettyPrint fillState.doc.updated_at ]
                ]
            ]
    <> catMaybes
        [ if busy then Just $ D.div
            [ P.className "loading-overlay step-3-loading-overlay" ]
            [ loadingWidget ]
          else Nothing
        ]
    where
    fillState = state.fillState

    semesterTranslation
        | fillState.doc.can.update = "semester-hidden-select"
        | otherwise = "semester-hidden-select-locked"

    formContextForBusy =
        let
            context = formContext state.workQueueCache state.fillState
        in
        if busy
            then context { access = { default: AccessView, fields: [] } }
            else context

formResultF :: FormFillState -> FormWidgetResult FormState -> FormFillResult
formResultF state (FormWidgetResult { state: fstate, action: Nothing }) =
    StateF $ state { form = fstate }
formResultF state (FormWidgetResult { state: fstate, action: Just act }) =
    ActionF act $ state { form = fstate }

previewStateChangeF :: FormFillState -> PreviewState -> FormFillResult
previewStateChangeF state pstate = StateF $ state { preview = pstate }

commentResultF :: FormFillState -> Comments.CommentsResult -> FormFillResult
commentResultF state = case _ of
    Comments.CommentsChanged cstate ->
        StateF $ state { comments = cstate }
    Comments.CommentsRequest request ->
        DocumentMutationF state $ case request of
            Comments.CreateComment input ->
                Step3Types.CreateComment state.doc.id input
            Comments.UpdateComment commentId input ->
                Step3Types.UpdateComment state.doc.id commentId input
            Comments.RemoveComment commentId ->
                Step3Types.DeleteComment state.doc.id commentId

attachmentResultF :: FormFillState -> Attachments.AttachmentsResult -> FormFillResult
attachmentResultF state = case _ of
    Attachments.AttachmentsChanged astate ->
        StateF $ state { attachments = astate }
    Attachments.AttachmentsRequest request ->
        DocumentMutationF state $ case request of
            Attachments.CreateAttachment input ->
                Step3Types.CreateAttachment state.doc.id input
            Attachments.RemoveAttachment attachmentId ->
                Step3Types.DeleteAttachment state.doc.id attachmentId

sigResultF :: FormFillState -> SigGroup.SigGroupResult -> FormFillResult
sigResultF state = case _ of
    SigGroup.SigGroupChanged sstate ->
        StateF $ state { signatures = sstate }
    SigGroup.SigGroupMutation sstate request ->
        DocumentMutationF (state { signatures = sstate }) $ case request of
            SigGroup.SignatureWidgetRequest widgetRequest ->
                case widgetRequest of
                    Signature.UseCapturedSignature field sig opts ->
                        Step3Types.SignWithCapturedSignature state.doc.id field sig opts
                    Signature.UseSavedSignature field ->
                        Step3Types.SignWithSavedSignature state.doc.id field
                    Signature.SendSignatureRequest signatureRequest ->
                        Step3Types.RequestSignatures [ signatureRequest ]
            SigGroup.SignatureEmailRequests requests ->
                Step3Types.RequestSignatures requests

metadataStateChangeF
    :: FillQueueState
    -> WorkQueueMetadataDraft
    -> FormFillResult
metadataStateChangeF previous next =
    if metadataChanged
        then WorkQueueMetadataF next.state next.ui
        else WorkQF $ UIStateChange next.ui
  where
    metadataChanged =
        (map _.id previous.workQueueState.teacher) /= (map _.id next.state.teacher)
            || (map _.semester previous.workQueueState.semester) /= (map _.semester next.state.semester)

setTeacherSelect
    :: FillQueueState
    -> Select SearchTeacher
    -> WorkQueueMetadataDraft
setTeacherSelect state teacher =
    { ui: state.workQueueUi { teacher = teacher }
    , state: state.workQueueState { teacher = _.value <$> selectedItem teacher }
    }

setSemesterSelect
    :: FillQueueState
    -> Select SearchSemester
    -> WorkQueueMetadataDraft
setSemesterSelect state semester =
    { ui: state.workQueueUi { semester = semester }
    , state: state.workQueueState { semester = _.value <$> selectedItem semester }
    }

renderLockBanner :: Boolean -> FormFillState -> Array (Widget HTML FormFillResult)
renderLockBanner busy fstate = case _.lockedBanner $ formEditability fstate of
    Nothing -> []
    Just variant ->
        let
            msg = lookupTranslation $ case variant of
                EditablePrivileged -> "form-locked-banner-message-mutable"
                EditableUnprivileged -> "form-locked-banner-message-mutable-unprivileged"
                UneditablePrivileged -> "form-locked-banner-message"
                UneditableUnprivileged -> "form-locked-banner-message-unprivileged"
        in [ D.div
        [ P.className "lock-banner" ] $
        [ D.div
            [ P.className "lock-banner-content" ]
            [ D.span
                [ P.className "lock-banner-icon", P.title $ lookupTranslation "locked" ]
                []
            , D.span
                [ P.className "lock-banner-message" ]
                [ D.text msg ]
            ]
        ] <> if bannerShowsUnlock variant
            then [ D.button
                ( [ P.disabled busy
                  , P._type "button"
                  , P.className "btn lock-banner-button"
                  ] <> if busy then [] else [ ToggleLockF <$ P.onClick ]
                )
                [ D.text $ lookupTranslation "unlock" ]
            ]
            else []
        ]
  where
    bannerShowsUnlock EditablePrivileged = true
    bannerShowsUnlock UneditablePrivileged = true
    bannerShowsUnlock _ = false
